// Tab strip for a record editor: the first tab (index 0) is the form being
// edited (always enabled); the rest are its direct child forms, enabled only
// once the parent record is saved. Shared by the top-level editor
// (LivePreviewTab) and every nested child-row editor (ChildFormTab), so the
// "save parent → manage children" gate is identical at any depth.

import { useState } from 'react'
import { Pencil } from 'lucide-react'
import { tw } from '../../testHooks'
import { useTxnCustomize } from '../../customize/txnContext'
import { formPath, leafDiff } from '../../customize/txnOps'
import { CustomizedDot, VIOLET } from '../../customize/ui'

interface FormTabsProps {
  forms: Array<{ formNo: string; sm: { form_title?: string } }>
  active: string
  onChange: (formNo: string) => void
  // When false, only the first (own) tab is enabled — the parent record isn't
  // saved yet, so its children can't be scoped to it.
  childTabsEnabled: boolean
  // Tooltip shown on a disabled child tab. Defaults to the header wording.
  disabledTitle?: string
}

export default function FormTabs({
  forms, active, onChange, childTabsEnabled,
  disabledTitle = 'Save the header first to manage child rows',
}: FormTabsProps) {
  // Customize mode: each tab's title can be renamed in place (form_title).
  const cz = useTxnCustomize()
  const [renaming, setRenaming] = useState<string | null>(null)
  const coreTitle = (formNo: string): string => {
    const forms0 = ((cz?.core as any)?.transaction?.sql_models || []) as any[]
    const f = forms0.map((w) => w?.sql_model ?? w).find((x) => String(x?.form_no ?? '') === formNo)
    return String(f?.form_title ?? '')
  }
  const commit = (formNo: string, v: string) => {
    setRenaming(null)
    if (!cz) return
    const std = coreTitle(formNo)
    const next = v.trim() || std
    void cz.session.apply(leafDiff(formPath(formNo, 'form_title'), next, std), `Rename form ${std}`)
  }
  return (
    <div style={{
      display: 'flex', gap: 0, alignItems: 'stretch',
      borderBottom: '1px solid var(--color-border)',
      background: 'var(--color-surface-2, var(--color-bg-secondary))',
      paddingLeft: 8,
    }}>
      {forms.map((f, idx) => {
        const isHeader = idx === 0
        const isActive = active === f.formNo
        const enabled = isHeader || childTabsEnabled
        const title = enabled ? undefined : disabledTitle
        if (cz && renaming === f.formNo) {
          return (
            <span key={f.formNo} style={{ display: 'flex', alignItems: 'center', padding: '4px 8px' }}>
              <input autoFocus defaultValue={f.sm.form_title || ''} aria-label="Form title"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') commit(f.formNo, (e.target as HTMLInputElement).value)
                  if (e.key === 'Escape') setRenaming(null)
                }}
                onBlur={(e) => commit(f.formNo, e.target.value)}
                style={{ height: 28, width: 160, border: `2px solid ${VIOLET}`, borderRadius: 5, padding: '0 8px', font: 'inherit', fontSize: '0.8825rem', fontWeight: 600 }} />
            </span>
          )
        }
        const renamed = !!cz && cz.session.isCustomized(formPath(f.formNo, 'form_title'))
        return (
          <span key={f.formNo} style={{ display: 'flex', alignItems: 'center' }}>
          <button
            key={f.formNo}
            type="button"
            {...tw('form', f.formNo, { tab: true, active: isActive })}
            disabled={!enabled}
            title={title}
            onClick={() => enabled && onChange(f.formNo)}
            style={{
              padding: '8px 14px',
              border: 'none',
              background: isActive ? 'var(--color-bg)' : 'transparent',
              borderBottom: isActive
                ? '2px solid var(--color-accent)'
                : '2px solid transparent',
              fontSize: '0.8825rem',
              fontWeight: isActive ? 600 : 500,
              color: enabled
                ? (isActive ? 'var(--color-text)' : 'var(--color-text-secondary)')
                : 'var(--color-text-tertiary, #9ca3af)',
              cursor: enabled ? 'pointer' : 'not-allowed',
              opacity: enabled ? 1 : 0.55,
            }}
          >
            {f.sm.form_title || `Form ${f.formNo}`}
          </button>
          {cz && (
            <span role="button" tabIndex={0} aria-label={`Rename ${f.sm.form_title || 'form'}`} title="Rename this form"
              onClick={() => setRenaming(f.formNo)}
              onKeyDown={(e) => { if (e.key === 'Enter') setRenaming(f.formNo) }}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: VIOLET, cursor: 'pointer', padding: '0 6px 0 0' }}>
              {renamed && <CustomizedDot />}<Pencil size={12} />
            </span>
          )}
          </span>
        )
      })}
    </div>
  )
}
